/**
 * Preloaded blog data shared between prerender.mjs's SSR pass and the
 * client's first paint, so both produce identical markup and React's
 * hydration never has to reconcile a "Loading..." placeholder against real
 * content (that mismatch is what previously threw React #418/#423 on every
 * /blog/ page and forced a full client-side re-render).
 *
 * Server (prerender.mjs, via entry-server.tsx): calls setPreloadedBlogList /
 * setPreloadedBlogPost with the same Supabase data it already fetched, right
 * before calling render(url) for that route.
 *
 * Client: prerender.mjs serializes that same data into a <script> tag
 * (window.__BLOG_LIST__ / window.__BLOG_POST__) in the page's <head>, which
 * runs before main.tsx's deferred module script hydrates the page.
 */
export interface PreloadedPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image_url: string | null;
  featured_image_alt?: string | null;
  featured_image_width?: number | null;
  featured_image_height?: number | null;
  author: string | null;
  author_name: string | null;
  author_bio: string | null;
  youtube_url: string | null;
  publish_date: string | null;
  updated_at: string | null;
  categories: string[];
  tags: string[];
  seo_title: string | null;
  seo_description: string | null;
  /** 'third_party' posts (publisher portal) live under /articles/, ours under /blog/. */
  publisher?: "ours" | "third_party";
}

declare global {
  interface Window {
    __BLOG_LIST__?: PreloadedPost[];
    __BLOG_POST__?: PreloadedPost;
    __HAS_ARTICLES__?: boolean;
  }
}

// Whether the partner's /articles/ section has any published post. Decided
// once per build by prerender.mjs (SSR) and written into every page as
// window.__HAS_ARTICLES__, so server and client agree. Hidden until then.
let ssrHasArticles = false;

export function setHasArticles(value: boolean): void {
  ssrHasArticles = value;
}

export function getHasArticles(): boolean {
  if (typeof window !== "undefined") return window.__HAS_ARTICLES__ === true;
  return ssrHasArticles;
}

// SSR-only holders. Set synchronously right before render(url) and cleared
// right after — safe because prerender.mjs's build script renders one route
// at a time, never concurrently.
let ssrList: PreloadedPost[] | null = null;
let ssrPost: PreloadedPost | null = null;

export function setPreloadedBlogList(list: PreloadedPost[] | null): void {
  ssrList = list;
}

export function setPreloadedBlogPost(post: PreloadedPost | null): void {
  ssrPost = post;
}

export function getPreloadedBlogList(): PreloadedPost[] | null {
  if (typeof window !== "undefined" && window.__BLOG_LIST__) {
    return window.__BLOG_LIST__;
  }
  return ssrList;
}

export function getPreloadedBlogPost(slug?: string): PreloadedPost | null {
  if (typeof window !== "undefined" && window.__BLOG_POST__ && (!slug || window.__BLOG_POST__.slug === slug)) {
    return window.__BLOG_POST__;
  }
  if (ssrPost && (!slug || ssrPost.slug === slug)) return ssrPost;
  return null;
}
